import React from 'react';
import { MapPin, Instagram, Facebook, Music2, Globe, Phone, MessageCircle, Ticket } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const LABELS = {
  es: { tickets: 'Entradas', maps: 'Google Maps', instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', web: 'Web', call: 'Llamar', whatsapp: 'WhatsApp' },
  en: { tickets: 'Tickets', maps: 'Google Maps', instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', web: 'Website', call: 'Call', whatsapp: 'WhatsApp' },
  de: { tickets: 'Tickets', maps: 'Google Maps', instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', web: 'Website', call: 'Anrufen', whatsapp: 'WhatsApp' },
};

const detectType = (url = '') => {
  const u = url.toLowerCase();
  if (u.includes('instagram.com')) return 'instagram';
  if (u.includes('facebook.com') || u.includes('fb.com') || u.includes('fb.me')) return 'facebook';
  if (u.includes('tiktok.com')) return 'tiktok';
  if (u.includes('wa.me') || u.includes('whatsapp.com')) return 'whatsapp';
  return 'web';
};

const ICONS = { tickets: Ticket, maps: MapPin, instagram: Instagram, facebook: Facebook, tiktok: Music2, web: Globe, call: Phone, whatsapp: MessageCircle };

const normalizeUrl = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

export const buildPlaceLinks = (place) => {
  const links = [];
  const seen = new Set();
  const add = (type, href) => {
    if (!href || seen.has(type)) return;
    seen.add(type);
    links.push({ type, href });
  };
  const mapsQuery = encodeURIComponent(place.name_es || `${place.latitude},${place.longitude}`);
  add('maps', place.google_place_id
    ? `https://www.google.com/maps/search/?api=1&query=${mapsQuery}&query_place_id=${place.google_place_id}`
    : `https://www.google.com/maps/search/?api=1&query=${place.latitude},${place.longitude}`);
  for (const raw of [place.instagram_url, place.facebook_url, place.tiktok_url, place.website]) {
    if (raw) add(detectType(raw), normalizeUrl(raw));
  }
  if (place.whatsapp) {
    const w = place.whatsapp.trim();
    add('whatsapp', /^https?:\/\//i.test(w) ? w : `https://wa.me/${w.replace(/[^\d]/g, '')}`);
  }
  if (place.phone) add('call', `tel:${place.phone.replace(/\s/g, '')}`);
  return links;
};

export const buildEventLinks = (event) => {
  const links = [];
  const seen = new Set();
  const hrefs = new Set();
  const add = (type, href) => {
    if (!href || seen.has(type) || hrefs.has(href)) return;
    seen.add(type); hrefs.add(href);
    links.push({ type, href });
  };
  if (event.ticket_url) add('tickets', normalizeUrl(event.ticket_url));
  const venue = `${event.venue_name || ''} ${event.venue_address || ''}`.trim();
  add('maps', venue
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue + ' Tenerife')}`
    : `https://www.google.com/maps/search/?api=1&query=${event.latitude},${event.longitude}`);
  for (const raw of [event.website, event.instagram_url, event.facebook_url, event.tiktok_url]) {
    if (raw) add(detectType(raw), normalizeUrl(raw));
  }
  if (event.whatsapp) {
    const w = event.whatsapp.trim();
    add('whatsapp', /^https?:\/\//i.test(w) ? w : `https://wa.me/${w.replace(/[^\d]/g, '')}`);
  }
  if (event.phone) add('call', `tel:${event.phone.replace(/\s/g, '')}`);
  return links;
};

export const PlaceLinks = ({ place, links: givenLinks, testId = 'place-links' }) => {
  const { language } = useLanguage();
  const t = LABELS[language] || LABELS.es;
  const links = givenLinks || buildPlaceLinks(place);

  return (
    <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-1" data-testid={testId}>
      {links.map(({ type, href }) => {
        const Icon = ICONS[type];
        const external = !href.startsWith('tel:');
        return (
          <a
            key={type}
            href={href}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            className="flex-shrink-0 w-[84px] h-[76px] rounded-2xl bg-[#38B2AC] text-white flex flex-col items-center justify-center gap-1.5 shadow-sm hover:bg-[#005A63] active:scale-95 transition-all"
            data-testid={`${testId.replace(/s$/, '')}-${type}`}
          >
            <Icon size={24} strokeWidth={2} />
            <span className="text-[11px] font-semibold leading-none">{t[type]}</span>
          </a>
        );
      })}
    </div>
  );
};

export default PlaceLinks;
